/**
 * Selectors to retrieve jupyter information for the current session go here.
 */
import { Kaggle } from "@kaggle/sdk";
import { ThemeName } from "@kaggle/styled-components";
import { createSelector } from "reselect";
import { getJupyterUrl } from "../../connector/extensions/utils/getJupyterUrl";
import { SharedState } from "../types/SharedState";
import { getCurrent } from "./current";
import { getSourceType } from "./session";
import { getThemeName } from "./theme";

import EditorType = Kaggle.Sdk.Kernels.EditorType;

/** Selects jupyter state from redux. */
export const getJupyterState = (state: SharedState) => state.jupyter;

/** Selects the current jupyter status. */
export const getJupyterStatus = createSelector(
  [getJupyterState],
  jupyter => jupyter.status
);

/**
 * Selects condensed Jupyter state that should be propagated up to the top frame.
 *
 * @remarks
 * In dev mode we don't need to pass all the jupyter events up the chain so this filters that out.
 */
export const getCondensedJupyterState = createSelector(
  [getJupyterStatus],
  status => ({
    status,
  })
);

/** Selects the Jupyter URL. */
export const getJupyterUrlWithQuery = createSelector(
  [getSourceType, getThemeName],
  (sourceType, themeName) => {
    const url = getJupyterUrl();
    if (!url) {
      return undefined;
    }
    // HACK(cochamberlain): This is a temporary means of signaling to the Jupyter
    // frame that it is a script and should be replaced with a better state
    // sharing mechanism.
    return `${url}${
      url.includes("?") ? "&" : "?"
    }sourceType=${sourceType}&themeName=${themeName}`;
  }
);

/** Selects the Jupyter Proxy URL. */
export const getJupyterProxyUrl = createSelector([getCurrent], current => {
  return current.jupyterProxyUrl;
});

// HACK(cochamberlain): This is a temporary means of retrieving whether we are
// in a notebook or a script from within the Jupyter frame. We should come up
// with a better pattern for sharing state across the divide.
export const getJupyterUrlSourceType = (url: string): EditorType =>
  url.includes("sourceType=script") ? EditorType.SCRIPT : EditorType.NOTEBOOK;

export const getJupyterUrlThemeName = (url: string): ThemeName | null =>
  url.includes("themeName=dark") ? "dark" : null;

// Check if Jupyter connection is established, by checking for the jupyter proxy url.
export const isJupyterConnected = createSelector(
  [getJupyterProxyUrl],
  jupyterProxyUrl => {
    return jupyterProxyUrl !== null;
  }
);
